import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ClipboardCheck, AlertTriangle, CheckCircle2, XCircle, MessageCircle, Mail, RefreshCw, Loader2, ChevronRight, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

export default function CierresMensuales() {
  const [cierres, setCierres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [editingItems, setEditingItems] = useState(null); // { cid, items }
  const [working, setWorking] = useState(null); // cid actuando

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cierres-mensuales");
      setCierres(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const dispararPropuestas = async () => {
    if (!window.confirm("¿Generar propuestas de cierre para todas las agencias con albaranes pendientes?\nNO se emiten facturas todavía — quedan en estado borrador para tu revisión.")) return;
    setRefreshing(true);
    try {
      const { data } = await api.post("/albaranes/_scheduler-cierre-mensual");
      toast.success(`${data.propuestas_creadas} propuesta${data.propuestas_creadas!==1?'s':''} creada${data.propuestas_creadas!==1?'s':''}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setRefreshing(false); }
  };

  const startEdit = (cierre) => {
    setEditingItems({ cid: cierre.id, items: cierre.items.map((it) => ({ ...it })) });
    setExpanded(cierre.id);
  };

  const saveEdit = async () => {
    if (!editingItems) return;
    setWorking(editingItems.cid);
    try {
      const { data } = await api.patch(`/cierres-mensuales/${editingItems.cid}/items`, { items: editingItems.items });
      toast.success("Cambios guardados");
      setEditingItems(null);
      // Refrescar la propuesta concreta
      setCierres((prev) => prev.map((c) => (c.id === editingItems.cid ? data : c)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setWorking(null); }
  };

  const cancelEdit = () => setEditingItems(null);

  const updateItemField = (idx, field, value) => {
    setEditingItems((prev) => {
      const items = prev.items.slice();
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const aprobar = async (cierre) => {
    const enviar = window.confirm(
      `¿Aprobar y emitir factura de ${cierre.cliente_nombre} por ${eur(cierre.totales.total)}?\n\n` +
      `Después se preguntará si quieres enviarla por WhatsApp/email.`
    );
    if (!enviar) return;
    const enviarWA = window.confirm("¿Generar link WhatsApp para enviar la factura al cliente?");
    const enviarEmail = false; // email queueing futuro
    const forzar = (cierre.warnings || []).length > 0
      ? window.confirm(`⚠️ Hay ${cierre.warnings.length} warning(s) (p.ej. precios 0). ¿Continuar de todas formas?`)
      : false;
    setWorking(cierre.id);
    try {
      const { data } = await api.post(`/cierres-mensuales/${cierre.id}/aprobar`, {
        enviar_whatsapp: enviarWA,
        enviar_email: enviarEmail,
        forzar,
      });
      toast.success(`Factura ${data.factura.numero} emitida`);
      if (data.enviado?.whatsapp_url) {
        window.open(data.enviado.whatsapp_url, "_blank", "noopener,noreferrer");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error aprobando");
    } finally { setWorking(null); }
  };

  const rechazar = async (cierre) => {
    const motivo = window.prompt("Motivo del rechazo (los albaranes quedarán para el próximo cierre):", "");
    if (motivo === null) return;
    setWorking(cierre.id);
    try {
      await api.post(`/cierres-mensuales/${cierre.id}/rechazar`, { motivo });
      toast.success("Propuesta rechazada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setWorking(null); }
  };

  const borradores = cierres.filter((c) => c.estado === "borrador");
  const procesados = cierres.filter((c) => c.estado !== "borrador").slice(0, 10);

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="cierres-mensuales-page">
      <PageHeader title="Cierres mensuales · Agencias" subtitle="Revisar y aprobar antes de facturar (Sara/Dani)" icon={ClipboardCheck} />

      <div className="flex justify-end">
        <Button onClick={dispararPropuestas} disabled={refreshing}
          data-testid="btn-disparar-propuestas"
          className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-bold">
          {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Generar propuestas ahora
        </Button>
      </div>

      {/* BORRADORES (lo importante) */}
      <div>
        <h3 className="text-sm font-semibold text-amber-300 mb-3 uppercase tracking-wider">
          Pendientes de revisión · {borradores.length}
        </h3>
        {loading ? (
          <div className="text-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando…</div>
        ) : borradores.length === 0 ? (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-10 text-center text-zinc-500 text-sm">
            <ClipboardCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No hay propuestas pendientes. Pulsa "Generar propuestas ahora" cuando quieras cerrar el mes.
          </div>
        ) : (
          <div className="space-y-3">
            {borradores.map((c) => {
              const isExp = expanded === c.id;
              const isEditing = editingItems?.cid === c.id;
              const items = isEditing ? editingItems.items : c.items;
              const warnings = c.warnings || [];
              return (
                <div key={c.id} className={`rounded-xl border ${warnings.length > 0 ? "border-rose-500/40 bg-rose-500/5" : "border-amber-500/40 bg-amber-500/5"}`}
                     data-testid={`cierre-${c.id}`}>
                  <div className="p-4 flex items-center justify-between gap-3 flex-wrap cursor-pointer"
                       onClick={() => setExpanded(isExp ? null : c.id)}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {isExp ? <ChevronDown className="w-5 h-5 text-amber-300" /> : <ChevronRight className="w-5 h-5 text-amber-300" />}
                      <div>
                        <div className="text-base font-bold text-zinc-100">{c.cliente_nombre || "Cliente"}</div>
                        <div className="text-xs text-zinc-400 mt-0.5">
                          {(c.albaran_numeros || []).length} albaranes · {(c.items || []).length} items ·{" "}
                          {warnings.length > 0 && (
                            <span className="text-rose-300 font-semibold">⚠️ {warnings.length} warning{warnings.length!==1?'s':''}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-amber-200">{eur(c.totales?.total)}</div>
                      <div className="text-[10px] text-zinc-500">IVA incl.</div>
                    </div>
                  </div>

                  {isExp && (
                    <div className="border-t border-zinc-700/50 p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                      {warnings.length > 0 && (
                        <div className="bg-rose-500/10 border border-rose-500/40 rounded-lg p-3 text-xs text-rose-200">
                          <div className="font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Warnings</div>
                          {warnings.map((w, i) => (
                            <div key={i}>· {w.albaran} — {w.item_descripcion} — {w.msg}</div>
                          ))}
                        </div>
                      )}

                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-[10px] uppercase text-zinc-500 border-b border-zinc-700">
                              <th className="py-1.5">Descripción</th>
                              <th className="py-1.5 text-right">Cantidad</th>
                              <th className="py-1.5 text-right">Precio €</th>
                              <th className="py-1.5 text-right">Dto %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(items || []).map((it, idx) => {
                              const precio = Number(it.precio_unitario || 0);
                              const precioCero = precio <= 0;
                              return (
                                <tr key={idx} className={precioCero ? "bg-rose-500/5" : ""}>
                                  <td className="py-1.5 text-zinc-200 pr-2">{it.descripcion || "(vacía)"}</td>
                                  <td className="py-1.5 text-right text-zinc-400">{it.cantidad} {it.unidad}</td>
                                  <td className="py-1.5 text-right">
                                    {isEditing ? (
                                      <Input type="number" step="0.01" min="0"
                                        value={it.precio_unitario ?? ""}
                                        onChange={(e) => updateItemField(idx, "precio_unitario", parseFloat(e.target.value) || 0)}
                                        data-testid={`edit-precio-${idx}`}
                                        className={`bg-zinc-900 border-zinc-700 h-7 w-24 text-right text-xs ${precioCero ? "border-rose-500/60" : ""}`} />
                                    ) : (
                                      <span className={precioCero ? "text-rose-300 font-bold" : "text-zinc-200"}>{eur(precio)}</span>
                                    )}
                                  </td>
                                  <td className="py-1.5 text-right text-zinc-400">{it.descuento_pct || 0}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-zinc-700/40">
                        {isEditing ? (
                          <>
                            <Button size="sm" variant="outline" onClick={cancelEdit} data-testid="btn-cancel-edit">Cancelar</Button>
                            <Button size="sm" onClick={saveEdit} disabled={working === c.id}
                              data-testid="btn-save-edit"
                              className="bg-emerald-500 hover:bg-emerald-600 text-white">
                              {working === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar precios"}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={() => startEdit(c)} data-testid={`btn-editar-${c.id}`}
                              className="border-zinc-600">✏️ Editar precios</Button>
                            <Button size="sm" variant="outline" onClick={() => rechazar(c)} disabled={working === c.id}
                              data-testid={`btn-rechazar-${c.id}`}
                              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Rechazar
                            </Button>
                            <Button size="sm" onClick={() => aprobar(c)} disabled={working === c.id}
                              data-testid={`btn-aprobar-${c.id}`}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold">
                              {working === c.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                              ✅ Facturar y enviar
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* HISTÓRICO */}
      {procesados.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-zinc-500 mb-3 uppercase tracking-wider">Histórico reciente</h3>
          <div className="space-y-1.5">
            {procesados.map((c) => (
              <div key={c.id} className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3 flex items-center justify-between text-xs"
                   data-testid={`historico-${c.id}`}>
                <div>
                  <span className="text-zinc-200 font-semibold">{c.cliente_nombre}</span>
                  <span className="text-zinc-500 ml-2">· {(c.albaran_numeros||[]).length} albaranes · {eur(c.totales?.total)}</span>
                </div>
                <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded border ${
                  c.estado === "aprobada" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" :
                  "bg-rose-500/20 text-rose-300 border-rose-500/40"
                }`}>{c.estado}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
